import { useMemo, useState } from 'react';
import { getPipelineCost } from '../lib/pipelineCost';
import { useIsMobile } from '../hooks/useIsMobile';
import { shortCouncilName } from '../config/registry';
import { getCategoryLabel, resolveProposalType } from '../lib/daCategories';
import { displayDAIdentifier, displayDAAddress } from '../lib/daDisplay';
import { getDwellings } from '../lib/daMeasurements';

// Desktop table view — first slice of the desktop UX program (13 June 2026).
// The office workhorse: dense sortable rows over the same filtered feed the
// cards render, click-through to the standard DA detail view. Desktop-only by
// design (cards remain the mobile experience); pure view-layer change — same
// data, same filters, same deep-linkable dashboard route, no new data access.

const COLUMNS = [
  { key: 'da_number', label: 'DA number', width: '12%' },
  { key: 'address', label: 'Address', width: '34%' },
  { key: 'council', label: 'Council', width: '10%' },
  { key: 'category', label: 'Category', width: '13%' },
  { key: 'status', label: 'Status', width: '13%' },
  { key: 'cost', label: 'Est. cost', width: '9%', align: 'right' },
  { key: 'dwellings', label: 'Dwellings', width: '7%', align: 'right' },
  { key: 'lodged_date', label: 'Lodged', width: '10%', align: 'right' },
];

function fmtMoney(v) {
  const value = Number(v || 0);
  if (!value) return '—';
  if (value >= 1e6) return `$${(value / 1e6).toFixed(value >= 1e8 ? 0 : 1)}M`;
  if (value >= 1e3) return `$${Math.round(value / 1e3)}K`;
  return `$${Math.round(value).toLocaleString()}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' });
}

function sortValue(app, key) {
  switch (key) {
    case 'cost': return getPipelineCost(app) || 0;
    case 'lodged_date': return app.lodged_date || '';
    case 'council': return shortCouncilName(app.council);
    case 'category': return resolveProposalType(app) || '';
    case 'dwellings': return Number(getDwellings(app) || 0);
    default: return String(app[key] || '').toLowerCase();
  }
}

const MAX_TABLE_ROWS = 200; // keep the DOM light; sort happens over the full set first

function tableBadge(theme, tone = 'muted') {
  const tones = {
    amber: { bg: `${theme.accent}18`, border: `${theme.accent}45`, color: theme.accent },
    green: { bg: 'rgba(90,186,106,0.14)', border: 'rgba(90,186,106,0.34)', color: '#5aba6a' },
    orange: { bg: 'rgba(212,112,64,0.14)', border: 'rgba(212,112,64,0.34)', color: '#d47850' },
    blue: { bg: 'rgba(93,152,230,0.14)', border: 'rgba(93,152,230,0.34)', color: '#7eb3f4' },
    muted: { bg: 'rgba(129,145,172,0.11)', border: `${theme.border}`, color: theme.textMuted },
  };
  const t = tones[tone] || tones.muted;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 22,
    padding: '3px 8px',
    borderRadius: 5,
    border: `1px solid ${t.border}`,
    background: t.bg,
    color: t.color,
    fontSize: 10,
    fontWeight: 850,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  };
}

function statusTone(status) {
  if (status === 'Approved' || status === 'Determined' || status === 'Deferred Commencement') return 'green';
  if (status === 'On Exhibition') return 'orange';
  if (status === 'Under Assessment') return 'amber';
  if (status === 'Lodged' || status === 'Submitted') return 'blue';
  return 'muted';
}

export default function DATableView({ apps, theme, onOpenDA, tradeMatches }) {
  const isMobile = useIsMobile(900); // table needs real width; cards below 900px
  const [sortKey, setSortKey] = useState('lodged_date');
  const [sortDir, setSortDir] = useState('desc');

  const totalCount = (apps || []).length;
  const sorted = useMemo(() => {
    const rows = [...(apps || [])];
    rows.sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows.slice(0, MAX_TABLE_ROWS);
  }, [apps, sortKey, sortDir]);

  if (isMobile) return null;

  const toggleSort = (key) => {
    if (key === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'address' ? 'asc' : 'desc'); }
  };

  return (
    <div
      data-testid="da-table-view"
      style={{
        background: theme.bgCard, border: `1px solid ${theme.border}`,
        borderRadius: 12, overflow: 'hidden', marginBottom: 12,
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: "'DM Sans',sans-serif" }}>
        <thead>
          <tr>
            {COLUMNS.map(col => (
              <th
                key={col.key}
                onClick={() => toggleSort(col.key)}
                role="columnheader"
                aria-sort={sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                style={{
                  width: col.width, textAlign: col.align || 'left', cursor: 'pointer', userSelect: 'none',
                  padding: '10px 12px', color: theme.textMuted, fontSize: 11,
                  textTransform: 'uppercase', letterSpacing: 0.5,
                  borderBottom: `1px solid ${theme.border}`, whiteSpace: 'nowrap',
                }}
              >
                {col.label}{sortKey === col.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(app => {
            const category = resolveProposalType(app);
            const dwellings = Number(getDwellings(app) || 0);
            const address = displayDAAddress(app, app.da_number || app.council_application_number || '—');
            return (
            <tr
              key={app.id}
              onClick={() => onOpenDA?.(app)}
              style={{ cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.background = `${theme.accent}0d`; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <td style={{ padding: '10px 12px', color: theme.textMuted, borderBottom: `1px solid ${theme.border}`, whiteSpace: 'nowrap', fontFamily: "'JetBrains Mono','SFMono-Regular',monospace", fontSize: 12 }}>
                {displayDAIdentifier(app, '—')}
              </td>
              <td style={{ padding: '10px 12px', color: theme.text, borderBottom: `1px solid ${theme.border}`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0, fontWeight: 750 }}>
                {address}
                {tradeMatches?.has(app.id) && <div style={{ color: theme.accent, fontSize: 12, fontWeight: 400, whiteSpace: 'normal', marginTop: 5 }}>{tradeMatches.get(app.id).tradeLabel} match: {tradeMatches.get(app.id).reason}</div>}
              </td>
              <td style={{ padding: '10px 12px', color: theme.textMuted, borderBottom: `1px solid ${theme.border}`, whiteSpace: 'nowrap' }}>{shortCouncilName(app.council)}</td>
              <td style={{ padding: '10px 12px', borderBottom: `1px solid ${theme.border}`, whiteSpace: 'nowrap' }}>
                <span style={tableBadge(theme, 'blue')}>{getCategoryLabel(category)}</span><div style={{ color: theme.textMuted, fontSize: 12, marginTop: 5, whiteSpace: 'normal' }}>Application: {app.application_type || 'Unknown'}</div>
              </td>
              <td style={{ padding: '10px 12px', borderBottom: `1px solid ${theme.border}`, whiteSpace: 'nowrap' }}>
                <span style={tableBadge(theme, statusTone(app.status))}>{app.status || '—'}</span>
              </td>
              <td style={{ padding: '10px 12px', color: theme.accent, borderBottom: `1px solid ${theme.border}`, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 850 }}>{fmtMoney(getPipelineCost(app))}</td>
              <td style={{ padding: '10px 12px', color: theme.textMuted, borderBottom: `1px solid ${theme.border}`, textAlign: 'right', whiteSpace: 'nowrap' }}>{dwellings || '—'}</td>
              <td style={{ padding: '10px 12px', color: theme.textMuted, borderBottom: `1px solid ${theme.border}`, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtDate(app.lodged_date)}</td>
            </tr>
          );})}
        </tbody>
      </table>
      {sorted.length === 0 && (
        <div style={{ padding: 16, color: theme.textMuted, fontSize: 13 }}>No DAs match the current filters.</div>
      )}
      {totalCount > MAX_TABLE_ROWS && (
        <div style={{ padding: '8px 12px', color: theme.textMuted, fontSize: 11, borderTop: `1px solid ${theme.border}` }}>
          Showing the first {MAX_TABLE_ROWS} of {totalCount.toLocaleString()} — narrow the filters or sort to bring rows into view.
        </div>
      )}
    </div>
  );
}
